<template>
  <ResubscribeViaSmsModal v-if="isResubscribeViaSmsModalOpen" />

  <ResubscribeViaWebModal
    v-else-if="isResubscribeViaWebModalOpen"
    @resubscribe:viaWeb="handleResubscribeViaWeb"
  />

  <ResubscribeViaPinModal
    v-else-if="isResubscribeViaPinModalOpen"
    @resubscribe:viaPin="handleResubscribeViaPin"
  />

  <ResubscribeSuccessModal
    v-else-if="isResubscribeSuccessModalOpen"
    @proceed:successResubscribe="handleProceedFromSuccessResubscribe"
  />

  <ResubscribeErrorModal v-else-if="isResubscribeErrorModalOpen" />
</template>

<script setup>
import { onMounted } from 'vue';
import ResubscribeViaSmsModal from '@/components/subscription/modal/ResubscribeViaSmsModal.vue';
import ResubscribeViaPinModal from '@/components/subscription/modal/ResubscribeViaPinModal.vue';
import ResubscribeViaWebModal from '@/components/subscription/modal/ResubscribeViaWebModal.vue';
import ResubscribeSuccessModal from '@/components/subscription/modal/ResubscribeSuccessModal.vue';
import ResubscribeErrorModal from '@/components/subscription/modal/ResubscribeErrorModal.vue';
import useAppSettingsStore from '@/store/modules/appSettings.js';
import usePageNavigationHook from '@/hooks/usePageNavigation.js';
import useModalHook from '@/hooks/useModal.js';

const emits = defineEmits(['resubscribe:resolved']);

const props = defineProps({
  lazy: {
    type: Boolean,
    default: false,
  },
  showInitialModalImmediate: {
    type: Boolean,
    default: false,
  },
});

const appSettingsStore = useAppSettingsStore();

const { redirectToHomePage } = usePageNavigationHook();
const [isResubscribeViaSmsModalOpen, openResubscribeViaSmsModal] = useModalHook();
const [isResubscribeViaWebModalOpen, openResubscribeViaWebModal, closeResubscribeViaWebModal] = useModalHook();
const [isResubscribeViaPinModalOpen, openResubscribeViaPinModal, closeResubscribeViaPinModal] = useModalHook();
const [isResubscribeSuccessModalOpen, openResubscribeSuccessModal, closeResubscribeSuccessModal] = useModalHook();
const [isResubscribeErrorModalOpen, openResubscribeErrorModal] = useModalHook();

const handleResubscribeViaWeb = async (result) => {
  await closeResubscribeViaWebModal({ immediate: true });

  if (result.status === 'success') {
    openResubscribeSuccessModal({ showImmediate: true, afterHideCallback: () => emits('resubscribe:resolved') });
  } else if (result.status === 'pinRequired') {
    openResubscribeViaPinModal({ showImmediate: true, afterHideCallback: () => emits('resubscribe:resolved') });
  } else {
    openResubscribeErrorModal({ showImmediate: true, afterHideCallback: () => emits('resubscribe:resolved') });
  }
};

const handleResubscribeViaPin = async (result) => {
  await closeResubscribeViaPinModal({ immediate: true });

  if (result.status === 'success') {
    openResubscribeSuccessModal({ showImmediate: true, afterHideCallback: () => emits('resubscribe:resolved') });
  } else {
    openResubscribeErrorModal({ showImmediate: true, afterHideCallback: () => emits('resubscribe:resolved') });
  }
};

const handleProceedFromSuccessResubscribe = async () => {
  await closeResubscribeSuccessModal({ immediate: true, afterHideCallback: () => emits('resubscribe:resolved') });

  redirectToHomePage();
};

const resubscribeUser = () => {
  if (appSettingsStore.isResubscribeViaSmsSupported) {
    openResubscribeViaSmsModal({ showImmediate: props.showInitialModalImmediate, afterHideCallback: () => emits('resubscribe:resolved') });
  } else {
    openResubscribeViaWebModal({ showImmediate: props.showInitialModalImmediate, afterHideCallback: () => emits('resubscribe:resolved') });
  }
};

onMounted(() => {
  if (!props.lazy) {
    resubscribeUser();
  }
});

defineExpose({
  resubscribeUser,
});
</script>
